/**
 * MALİYET ONAY DİYALOĞU — pahalı motorlar başlamadan önce kullanıcıya ne kadar TOKEN
 * harcayacağını söyler ve onay ister. Kullanıcıya dolar GÖSTERİLMEZ (1000 token = 1 birim).
 *
 * Kullanım (tek satır):
 *   if (!(await confirmCost("planner"))) return;
 *
 * Promise tabanlı: tek bir <CostConfirmHost /> App kökünde durur, her çağrı onu açar.
 * "Bu oturumda tekrar sorma" seçeneği aynı motor için oturum boyunca onayı hatırlar.
 */
import { useSyncExternalStore } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Coins } from "lucide-react";

export type EngineKey =
  | "planner" | "writer" | "ai-visibility" | "ai-overview" | "strengthen"
  | "page-scan" | "tech-audit" | "backlink" | "translate" | "quick-analysis" | "crawl" | "action-plan"
  | "rank-tracking" | "rank-suggest" | "seo-report" | "seo-report-live";

// Ölçülmüş gerçek maliyetlerden türetilmiş token tahminleri (1000 token = 1 birim maliyet)
const ENGINES: Record<EngineKey, { title: string; tokens: number; what: string; heavy?: boolean }> = {
  planner: { title: "İçerik Keşif Ajanı", tokens: 3000, heavy: true, what: "Rakip siteleri okur, kelime boşluklarını bulur ve yeni içerik brief'leri üretir." },
  writer: { title: "İçerik Üretimi", tokens: 3000, heavy: true, what: "Brief'ten tam makaleyi yazar, kalite denetçisinden geçirir." },
  "ai-visibility": { title: "AI Görünürlük Analizi", tokens: 750, heavy: true, what: "ChatGPT, Perplexity ve Gemini'ye onlarca soru sorup markanın anılma oranını ölçer." },
  "ai-overview": { title: "AI Overview Takibi", tokens: 500, what: "Google AI Overview sonuçlarında kaynak gösterilme durumunu tarar." },
  strengthen: { title: "Sayfa Güçlendirme", tokens: 750, what: "Sayfanın tüm verisini okuyup içerik + link revize planı hazırlar." },
  "page-scan": { title: "Detaylı AI Sayfa Taraması", tokens: 200, what: "Sayfayı yeniden tarar ve AI linkleme planı çıkarır." },
  "tech-audit": { title: "Teknik SEO Denetimi", tokens: 150, what: "Site genelinde teknik hataları, schema ve güvenlik başlıklarını denetler." },
  backlink: { title: "Backlink Denetimi", tokens: 200, what: "Backlink profilini ve rakip backlink fırsatlarını analiz eder." },
  translate: { title: "İçerik Çevirisi", tokens: 1000, heavy: true, what: "İçeriği hedef pazara uyarlayarak çevirir (görseller ve linkler dahil)." },
  "quick-analysis": { title: "Hızlı Analiz", tokens: 500, what: "Siteyi hızlıca tarayıp özet SEO/GEO raporu çıkarır." },
  crawl: { title: "Site Taraması", tokens: 1000, what: "Tüm siteyi tarar, cluster yapısını ve link grafiğini oluşturur." },
  "action-plan": { title: "Aksiyon Planı", tokens: 100, what: "Tüm ajan verilerini okuyup öncelikli yapılacaklar listesi çıkarır." },
  "rank-tracking": { title: "Sıralama Takibi", tokens: 300, what: "Takip listesindeki her kelime için canlı arama sonucu çeker ve her soruyu 4 AI motoruna sorar." },
  "rank-suggest": { title: "Kelime Önerisi", tokens: 60, what: "Sitenin hâlihazırda sıralandığı kelimeleri getirir — takip listesine eklemek için." },
  "seo-report": { title: "SEO-GEO Raporu (kayıtlı site)", tokens: 100, what: "Mevcut ajan verilerini rapora derler ve rakip kıyas tablosunu canlı çeker." },
  "seo-report-live": { title: "SEO-GEO Raporu (dış site)", tokens: 800, heavy: true, what: "Siteyi baştan tarar: 100 sayfa teknik denetim + hız + backlink profili + AI görünürlük + rakipler. Birkaç dakika sürer." },
};

/** Hesabım sayfasındaki "işlem başına token" fiyat listesi aynı katalogdan beslenir. */
export const ENGINE_PRICES = ENGINES;

type Pending = { engine: EngineKey; extra?: string; resolve: (ok: boolean) => void };
let pending: Pending | null = null;
const skip = new Set<EngineKey>(); // "tekrar sorma" işaretlenen motorlar (oturum boyu)
const subs = new Set<() => void>();
const emit = () => subs.forEach((f) => f());

/** Pahalı motoru başlatmadan önce çağır: onay verilirse true döner. */
export function confirmCost(engine: EngineKey, extra?: string): Promise<boolean> {
  if (skip.has(engine)) return Promise.resolve(true);
  return new Promise<boolean>((resolve) => {
    pending = { engine, extra, resolve };
    emit();
  });
}

function usePending(): Pending | null {
  return useSyncExternalStore(
    (cb) => { subs.add(cb); return () => subs.delete(cb); },
    () => pending,
  );
}

export function CostConfirmHost() {
  const p = usePending();
  if (!p) return null;
  const e = ENGINES[p.engine];
  const close = (ok: boolean, remember = false) => {
    if (ok && remember) skip.add(p.engine);
    const resolve = p.resolve;
    pending = null;
    emit();
    resolve(ok);
  };

  // RENKLER: marka paleti (brand-*) — eskiden sabit turuncu hex'ler (#b54708/#f79009/#fffbeb)
  // yazılıydı; hem uygulamanın geri kalanıyla uyuşmuyordu hem de sabit AÇIK arka plan
  // koyu temada beyaz kutu olarak patlıyordu. Artık tüm tonlar tema değişkenlerinden.
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 120, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={() => close(false)}>
      <Card className="border-brand-500/50" style={{ maxWidth: 460, width: "100%" }} onClick={(ev) => ev.stopPropagation()}>
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-1">
            <Coins className="w-5 h-5 text-brand-500 dark:text-brand-400" />
            <span className="font-bold">{e.title} başlatılsın mı?</span>
          </div>
          <p className="text-[13px] text-muted-foreground mt-1">{e.what}</p>
          {p.extra && <p className="text-[13px] mt-1.5 font-medium">{p.extra}</p>}

          <div className="mt-3 rounded-xl px-3 py-2.5 flex items-center justify-between bg-brand-50 dark:bg-brand-500/[0.12] border border-brand-500/30">
            <span className="text-[12px] text-muted-foreground">Tahmini token kullanımı</span>
            <span className="font-bold text-brand-600 dark:text-brand-400">~{e.tokens.toLocaleString("tr-TR")} token</span>
          </div>

          {e.heavy && (
            <div className="mt-2 flex items-start gap-1.5 text-[11px] text-brand-600 dark:text-brand-400">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              Bu, en yüksek maliyetli işlemlerden biri. Aynı işi tekrar başlatmak tokenı yeniden harcar — bitmesini bekle.
            </div>
          )}

          <div className="flex items-center justify-between gap-2 mt-4 flex-wrap">
            <button onClick={() => close(true, true)} className="text-[11px] underline text-muted-foreground">
              Onayla, bu oturumda tekrar sorma
            </button>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => close(false)}>Vazgeç</Button>
              {/* Varsayılan buton zaten bg-primary (brand-500) — ayrı gradient vermeye gerek yok */}
              <Button size="sm" onClick={() => close(true)}>Başlat</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
